import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Define public routes that don't require authentication
const PUBLIC_ROUTES = ['/login', '/signup', '/', '/features', '/about', '/terms', '/privacy', '/pricing'];

// Cache for responses to avoid repeated network requests
const responseCache = new Map<string, {
  data: any;
  timestamp: number;
  expiresIn: number;
}>();

export const CACHE_TTL = {
  SHORT: 5 * 1000,      // 5 seconds
  MEDIUM: 15 * 1000,    // 15 seconds
  LONG: 2 * 60 * 1000   // 2 minutes
};

// Validate URL format to catch obvious issues early
if (supabaseUrl && supabaseAnonKey) {
  try {
    new URL(supabaseUrl);
  } catch (e) {
    console.error('Invalid Supabase URL format:', e);
  }
} else {
  console.error('Missing Supabase environment variables.');
}

// Cache key generator for requests
const getCacheKey = (resource: RequestInfo, config: RequestInit = {}): string => {
  const url = typeof resource === 'string' ? resource : resource.url;
  const method = config.method || 'GET';
  
  // Only cache GET requests
  if (method !== 'GET') return '';
  
  // Don't cache auth-related requests
  if (url.includes('/auth/')) return '';
  
  // Generate a cache key from the URL and any query params
  return `${method}:${url}`;
};

// Function to determine cache TTL based on the endpoint
const getCacheTTL = (url: string): number => {
  // User data needs to be fairly fresh
  if (url.includes('/profiles')) return CACHE_TTL.SHORT;
  
  // Analysis data can be cached a bit longer
  if (url.includes('/content_analyses')) return CACHE_TTL.MEDIUM;
  
  // Plan and subscription data rarely changes
  if (url.includes('/user_plans') || url.includes('/user_subscriptions') || url.includes('/stripe_user_subscriptions')) return CACHE_TTL.SHORT;
  
  // Default to short cache time
  return CACHE_TTL.SHORT;
};

// Check and get cached response
const getCachedResponse = (resource: RequestInfo, config: RequestInit = {}): Response | null => {
  const cacheKey = getCacheKey(resource, config);
  if (!cacheKey) return null;
  
  const cached = responseCache.get(cacheKey);
  if (!cached) return null;
  
  const now = Date.now();
  if (now - cached.timestamp > cached.expiresIn) {
    // Cache expired
    responseCache.delete(cacheKey);
    return null;
  }
  
  return new Response(JSON.stringify(cached.data), {
    status: 200,
    headers: { 'Content-Type': 'application/json' }
  });
};

// Store response in cache
const cacheResponse = async (resource: RequestInfo, config: RequestInit = {}, response: Response): Promise<void> => {
  const cacheKey = getCacheKey(resource, config);
  if (!cacheKey) return;
  
  try {
    const url = typeof resource === 'string' ? resource : resource.url;
    const clonedResponse = response.clone();
    const data = await clonedResponse.json();
    
    responseCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      expiresIn: getCacheTTL(url)
    });
  } catch (error) {
    console.error('Error caching response:', error);
  }
};

// Enhanced fetch with retry logic and better error handling
const enhancedFetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
  const [resource, config = {}] = args;
  const maxRetries = 3; // Increased from 2 to 3 for better reliability

  // Check if we're on a public route
  const isPublicRoute = PUBLIC_ROUTES.some(route => 
    window.location.pathname === route || 
    window.location.pathname.startsWith(route + '/')
  );
  
  // Check if this is a logout request and use longer timeout
  const isLogoutRequest = typeof resource === 'string' && resource.includes('/auth/v1/logout');
  const isAuthRequest = typeof resource === 'string' && resource.includes('/auth/v1/');
  const isTokenRefreshRequest = typeof resource === 'string' && 
    resource.includes('/auth/v1/token') && 
    resource.includes('grant_type=refresh_token');
  
  // Determine if this is a storage upload request
  const isStorageUpload = typeof resource === 'string' && 
    resource.includes('/storage/v1/object') && 
    (config.method === 'POST' || config.method === 'PUT');
  
  // Set appropriate timeouts based on request type
  const maxTimeout = isStorageUpload ? 300000 : // 5 minutes for storage uploads
                    isLogoutRequest ? 8000 : 
                    isAuthRequest ? 10000 : 
                    8000;
  
  let lastError: Error | null = null;
  let attempt = 0;

  // For debugging: Log the request
  const requestUrl = typeof resource === 'string' ? resource : resource.url;
  console.log(`[Fetch] Requesting ${requestUrl}, timeout: ${maxTimeout}ms, retry: ${maxRetries}`);

  // Skip token refresh requests on public routes
  if (isTokenRefreshRequest && isPublicRoute) {
    console.log(`[Fetch] Skipping token refresh on public route: ${window.location.pathname}`);
    return new Response(JSON.stringify({ 
      error: 'skipped_on_public_route',
      message: 'Token refresh skipped on public route'
    }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Check cache first for GET requests
  const cachedResponse = getCachedResponse(resource, config);
  if (cachedResponse) {
    console.log(`[Fetch] Using cached response for ${requestUrl}`);
    return cachedResponse;
  }

  // Try to fetch with retries
  while (attempt < maxRetries) {
    attempt++;
    
    try {
      console.log(`[Fetch] Attempt ${attempt}/${maxRetries} for ${requestUrl}`);
      
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Request to ${requestUrl} timed out after ${maxTimeout}ms`));
        }, maxTimeout);
      });
      
      // Execute the fetch with timeout
      const response: Response = await Promise.race([fetch(resource, config), timeoutPromise]);
      
      // Error handling for common HTTP error status codes
      if (!response.ok) {
        const status = response.status;
        let errorText = '';
        let errorData = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              errorData = await response.clone().json();
              errorText = JSON.stringify(errorData);
            } catch (parseErr) {
              errorText = await response.clone().text() || 'Could not parse JSON error response';
            }
          } else {
            errorText = await response.clone().text() || 'No error details available';
          }
        } catch (e) {
          errorText = 'Could not parse error response';
        }
        
        console.error(`Supabase request failed with status ${status}:`, {
          url: requestUrl,
          status,
          body: errorText,
          data: errorData
        });

        // Skip refresh token errors on public routes
        if (isPublicRoute && 
            (errorText.includes('refresh_token_not_found') || 
             (errorData?.code === 'refresh_token_not_found'))) {
          console.log('Ignoring refresh token error on public route');
          return new Response(JSON.stringify({ 
            error: 'refresh_token_not_found',
            message: 'Refresh token not found, but on public route'
          }), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          });
        }

        // Check for refresh token errors first
        if (errorText.includes('refresh_token_not_found') || 
            (errorData?.code === 'refresh_token_not_found')) {
          console.error('Refresh token not found error detected');
          // Clear auth cache and throw specific error
          clearAuthCache();
          const error = new Error('refresh_token_not_found');
          (error as any).statusCode = status;
          (error as any).details = errorText;
          throw error;
        }

        // Handle other auth errors (401/403)
        if (status === 401 || status === 403) {
          const error = new Error(`Authentication error: ${status}`);
          (error as any).statusCode = status;
          (error as any).details = errorText;
          throw error;
        }
        
        // For 400 (bad request) specific to RLS issues with profiles table
        if (status === 400) {
          // Don't retry bad requests
          const error = new Error(`Bad request: ${errorText}`);
          (error as any).statusCode = status;
          (error as any).details = errorText;
          
          if (requestUrl.toString().includes('profiles')) {
            console.error('Possible RLS policy issue with profiles table');
          }
          
          throw error;
        }
        
        // For other errors, we should retry
        if (attempt < maxRetries) {
          const delay = Math.min(2 ** (attempt - 1) * 700, 3000);
          console.warn(`HTTP error ${status}, retrying in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // If this is our last attempt, return the error response
        return response;
      }
      
      console.log(`[Fetch] Successfully completed request to ${requestUrl}`);
      
      // Cache the successful response for future use
      if (config.method !== 'POST' && config.method !== 'PUT' && config.method !== 'DELETE') {
        await cacheResponse(resource, config, response.clone());
      }
      
      return response;
    } catch (err) {
      // Log the actual error for debugging
      console.error(`[Fetch] Error during attempt ${attempt}:`, err);
      
      // If this is a refresh token error, throw it immediately
      if (err instanceof Error && err.message === 'refresh_token_not_found') {
        throw err;
      }
      
      lastError = err instanceof Error ? err : new Error(String(err));
      
      // For network errors or timeouts, retry with exponential backoff
      if (err instanceof TypeError || 
          (err instanceof Error && err.message?.includes('timed out')) || 
          (err instanceof Error && err.message?.includes('fetch'))) {
        // Use more aggressive backoff for storage uploads
        const baseDelay = isStorageUpload ? 1500 : 700;
        const maxDelay = isStorageUpload ? 10000 : 3000;
        const delay = Math.min(2 ** (attempt - 1) * baseDelay, maxDelay);
        
        console.warn(`Fetch attempt ${attempt} failed, retrying in ${delay}ms:`, err.message);
        
        // Don't retry if we've reached max attempts
        if (attempt >= maxRetries) {
          break;
        }
        
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // For other errors, don't retry
      throw err;
    }
  }
  
  // If we got here and it's not a Supabase request, we need to throw the error
  if (lastError) {
    throw lastError;
  }
  
  // Fallback error if somehow we get here without an error
  throw new Error('Max retries exceeded');
};

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage,
    storageKey: 'supabase.auth.token',
  },
  // Add global error handler for network issues
  global: {
    fetch: enhancedFetch
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Helper function to validate Supabase connection
export const testSupabaseConnection = async (): Promise<boolean> => {
  try {
    // Simple query to check if we can connect to Supabase
    const { error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1);
      
    if (error) {
      console.error('Supabase connection test failed:', error);
      return false;
    }
    
    console.log('Supabase connection successful');
    return true;
  } catch (err) {
    console.error('Failed to connect to Supabase:', err);
    
    return false;
  }
};

// Function to clear the response cache
export const clearResponseCache = (pattern?: string): void => {
  if (pattern) {
    // Clear only keys matching the pattern
    for (const key of responseCache.keys()) {
      if (key.includes(pattern)) {
        responseCache.delete(key);
      }
    }
    console.log(`Response cache cleared for pattern: ${pattern}`);
  } else {
    // Clear all cache
    responseCache.clear();
    console.log('Response cache cleared');
  }
};

// Clear auth-related cache specifically
export const clearAuthCache = (): void => {
  clearResponseCache('/auth/v1/');
  clearResponseCache('/user_subscriptions');
  clearResponseCache('/profiles');
  console.log('Auth cache cleared');
};

// Function to clear all caches
export const clearAllCaches = (): void => {
  responseCache.clear();
  
  // Clear any auth-related localStorage items
  localStorage.removeItem('supabase.auth.token');
  localStorage.removeItem('supabase.auth.expires_at');
  localStorage.removeItem('supabase.auth.refresh_token');
  localStorage.removeItem('subscriptionCache');
  localStorage.removeItem('current_subscription_id');
  localStorage.removeItem('subscription_end_date');
  
  console.log('All caches cleared');
};

// Function to delete all videos for a user
export const deleteUserVideos = async (userId: string): Promise<{ success: boolean; message: string }> => {
  try {
    console.log(`Calling delete-user-videos function for user: ${userId}`);
    
    // Add retry logic for better reliability
    const maxRetries = 3;
    let retryCount = 0;
    let lastError: any = null;
    
    while (retryCount <= maxRetries) {
      try {
        // Call the delete-user-videos Edge Function
        const response = await fetch(`${supabaseUrl}/functions/v1/delete-user-videos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token || supabaseAnonKey}`
          },
          body: JSON.stringify({ user_id: userId })
        });
        
        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {
            errorData = { error: 'Failed to parse error response' };
          }
          console.error('Error deleting user videos:', errorData);
          return { 
            success: false, 
            message: errorData.error || 'Failed to delete user videos' 
          };
        }
        
        let result;
        try {
          result = await response.json();
        } catch (e) {
          result = { message: 'Videos deleted but response could not be parsed' };
        }
        console.log('Videos deleted successfully:', result);
        
        return { 
          success: true, 
          message: result.message || 'Videos deleted successfully' 
        };
      } catch (error) {
        console.error('Error deleting user videos:', error);
        return { 
          success: false, 
          message: 'Failed to delete videos. Please try again later.' 
        };
      }
    }
    throw new Error('Max retries exceeded');
  } catch (error) {
    console.error('Error in deleteUserVideos:', error);
    return {
      success: false,
      message: 'An error occurred while deleting videos. Please try again later.'
    };
  }
};

// Function to fix user subscription issues by calling the server function
export const fixUserSubscription = async (): Promise<any> => {
  try {
    console.log('Calling admin_fix_user_subscription...');
    const { data, error } = await supabase.rpc('admin_fix_user_subscription');
    
    if (error) {
      console.error('Error fixing user subscription:', error);
      throw error;
    }
    
    console.log('User subscription fixed:', data);
    // Clear response cache to ensure fresh data
    clearResponseCache();
    return data;
  } catch (err) {
    console.error('Failed to fix user subscription:', err);
    throw err;
  }
};